import { Component, OnInit, Inject, ChangeDetectorRef, ElementRef, ViewChild, ViewChildren, QueryList } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatSelectModule } from '@angular/material/select';
import { MatOption, MatOptionModule } from '@angular/material/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { CommonModule, NgIf, NgFor } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { RRule, Frequency } from 'rrule';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TokenStorageService } from 'src/app/services/token-storage.service';
import { environment } from 'src/environments/environment';
import { UserDTO } from 'src/app/interfaces/user';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { EventDTO } from 'src/app/interfaces/event';
import { CalendarDTO } from 'src/app/interfaces/calendar';
import { CalendarService } from 'src/app/services/calendar.service';
import { firstValueFrom } from 'rxjs';
import { RecurrenceDialogComponent } from '../recurrence-dialog/recurrence-dialog.component';

@Component({
  selector: 'app-event',
  standalone: true,
  imports: [
    CommonModule, NgIf, NgFor, FormsModule, ReactiveFormsModule,
    MatSelectModule, MatOptionModule, MatDatepickerModule, MatIconModule,
    MatToolbarModule, MatFormFieldModule, MatInputModule, MatButtonModule,
    MatCheckboxModule, MatRadioModule, MatChipsModule, MatAutocompleteModule,
    MatTooltipModule
  ],
  templateUrl: './event.component.html',
  styleUrls: ['./event.component.scss']
})
export class EventComponent implements OnInit {
  @ViewChild('editorElement') set editorElement(element: ElementRef<HTMLDivElement>) {
    if (element && !this.editor) {
      this.initializeEditor(element.nativeElement);
    } else if (element && this.editor) {
      this.editor.commands.setContent(this.event?.description || '');
    }
  }
  @ViewChildren(MatOption) optionElements!: QueryList<MatOption>;


  public editor!: Editor;
  calendars: CalendarDTO[];
  attendees: string[] = [];
  isAllDay: boolean = false;
  timeSlots: string[] = [];

  event: EventDTO = {
    id: -1,
    eventId: '',
    recurringEventId: '',
    title: '',
    isRecurring: false,
    startDate: new Date(),
    endDate: new Date(Date.now() + 30 * 60 * 1000),
    originalMeetingTime: null,
    timeZone: 'Europe/Amsterdam',
    attendees: '',
    location: '',
    attendeeNames: '',
    organizer: '',
    description: '',
    descriptionType: 'text',
    calendarId: -1,
    rrule: '',
    exdate: '',
    recurrenceId: null,
    status: ''
  };

  // Form State
  startTimeStr = '09:00';
  endTimeStr = '10:00';
  recurrenceFrequency: 'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY' = 'NONE';
  recurrenceEndType: 'NEVER' | '6' | '12' = 'NEVER';
  editScope: 'single' | 'series' = 'single';
  isEditMode = false;
  recurrenceSummaryText = '';

  readonly separatorKeysCodes = [ENTER, COMMA] as const;

  isLoggedIn: boolean = false;
  user!: UserDTO;
  role: string = '';
  isOk: boolean = false;
  envClass = 'prod';

  private originalMasterStartDate: Date = new Date();
  private originalMasterEndDate: Date = new Date();

  constructor(
    private router: Router,
    private tokenStorageService: TokenStorageService,
    public dialogRef: MatDialogRef<EventComponent>,
    @Inject(MAT_DIALOG_DATA) data: EventDTO,
    private matDialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private calendarService: CalendarService
  ) {
    this.generateTimeSlots();
    if (data) {
      this.isEditMode = !!(data.id && data.id > 0);
      this.event = { ...data }; // Maak een kopie om directe mutatie te voorkomen

      // Bewaar de originele datums voor wanneer de gebruiker de HELE reeks aanpast
      this.originalMasterStartDate = data.startDate ? new Date(data.startDate) : new Date();
      this.originalMasterEndDate = data.endDate ? new Date(data.endDate) : new Date();

      this.isAllDay = this.isAllDayEvent(data);
      this.parseAttendees();
      this.parseExistingDates();
      this.parseExistingRRule()
    }
  }

  ngOnInit(): void {
    this.envClass = `env-${environment.name}`;
    this.isAuthorized();
    this.getCalendars();
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  isAuthorized() {
    this.isLoggedIn = !!this.tokenStorageService.getToken();
    if (this.isLoggedIn) {
      this.user = this.tokenStorageService.getUser();
      this.role = this.user.role;
      this.isOk = true;
    } else {
      this.isOk = false;
    }
    if (!this.isOk) {
      this.router.navigate(['home']);
    }
  }

  initializeEditor(element: HTMLElement) {
    this.editor = new Editor({
      element: element,
      extensions: [StarterKit],
      content: this.event?.description || '',
      onUpdate: ({ editor }) => {
        if (this.event) {
          this.event.description = editor.getHTML();
        }
      },
    });
    this.cdr.detectChanges();
  }

  private generateTimeSlots() {
    const slots: string[] = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 15) {
        slots.push(`${this.padZero(h)}:${this.padZero(m)}`);
      }
    }
    this.timeSlots = slots;
  }

  async getCalendars() {
    if (this.user) {
      this.calendars = await firstValueFrom(this.calendarService.getAllCalendars());
    }
  }

  getCalendarLabel(calendar: CalendarDTO): string {
    if (!calendar) return '';
    const duplicates = this.calendars.filter(c => c.summary === calendar.summary);
    if (duplicates.length > 1 && calendar.calendarIntegrationName) {
      return `${calendar.summary} (${calendar.calendarIntegrationName})`;
    }
    return calendar.summary;
  }

  getSelectedCalendar(): CalendarDTO | undefined {
    if (!this.calendars || !this.event?.calendarId) return undefined;
    return this.calendars.find(c => c.id === this.event.calendarId);
  }

  isAllDayEvent(event: EventDTO): boolean {

    const start = new Date(event.startDate);
    const end = new Date(event.endDate);

    // Controleer of start en eind op 00:00 vallen en minimaal 24u verschillen
    const isStartMidnight = start.getHours() === 0 && start.getMinutes() === 0;
    const isEndMidnight = end.getHours() === 0 && end.getMinutes() === 0;

    return isStartMidnight && isEndMidnight && (end.getTime() - start.getTime() >= 86400000);
  }

  addAttendee(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    if (value && this.validateEmail(value)) {
      if (!this.attendees) {
        this.attendees = [];
      }
      this.attendees.push(value);
    }

    if (event.chipInput) {
      event.chipInput.clear();
    }
  }

  removeAttendee(email: string): void {
    if (!this.attendees) return;
    const index = this.attendees.indexOf(email);
    if (index >= 0) {
      this.attendees.splice(index, 1);
    }
  }

  private validateEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  // Date/Time Parsing Helpers
  private parseExistingDates() {
    if (this.event.startDate) {
      const start = new Date(this.event.startDate);
      // Rond af naar het dichtstbijzijnde kwartier voor de dropdown
      this.startTimeStr = `${this.padZero(start.getHours())}:${this.padZero(Math.floor(start.getMinutes() / 15) * 15)}`;
    }
    if (this.event.endDate) {
      const end = new Date(this.event.endDate);
      this.endTimeStr = `${this.padZero(end.getHours())}:${this.padZero(Math.floor(end.getMinutes() / 15) * 15)}`;
    }
  }

  private parseAttendees() {
    if (this.event.attendees) {
      this.attendees = this.event.attendees.split(',').map(a => a.trim()).filter(a => a.length > 0);
    }
  }

  private parseExistingRRule() {
    if (!this.event.rrule) return;
    try {
      const rule = RRule.fromString(this.event.rrule);
      switch (rule.options.freq) {
        case Frequency.DAILY: this.recurrenceFrequency = 'DAILY'; break;
        case Frequency.WEEKLY: this.recurrenceFrequency = 'WEEKLY'; break;
        case Frequency.MONTHLY: this.recurrenceFrequency = 'MONTHLY'; break;
        case Frequency.YEARLY: this.recurrenceFrequency = 'YEARLY'; break;
      }
      if (rule.options.count === 6) this.recurrenceEndType = '6';
      else if (rule.options.count === 12) this.recurrenceEndType = '12';
      else this.recurrenceEndType = 'NEVER';
    } catch (e) {
      console.error('Error parsing RRule:', e);
    }
  }

  private generateRRule(startDate: Date): string | undefined {
    if (this.recurrenceFrequency === 'NONE') {
      return undefined;
    }

    let freq: Frequency;
    switch (this.recurrenceFrequency) {
      case 'DAILY': freq = Frequency.DAILY; break;
      case 'WEEKLY': freq = Frequency.WEEKLY; break;
      case 'MONTHLY': freq = Frequency.MONTHLY; break;
      case 'YEARLY': freq = Frequency.YEARLY; break;
    }

    const options: any = {
      freq: freq,
      dtstart: startDate
    };

    if (this.recurrenceEndType === '6') options.count = 6;
    if (this.recurrenceEndType === '12') options.count = 12;

    const rule = new RRule(options);
    return rule.toString();
  }

  async save() {
    const isExistingRecurring = !!(this.event.isRecurring || this.event.rrule || this.event.recurringEventId);
    let chosenScope: 'THIS_INSTANCE' | 'ALL_EVENTS' = 'ALL_EVENTS';

    // 1. Vraag de gebruiker om de scope als het een herhalend event betreft
    if (this.isEditMode && isExistingRecurring) {
      const scopeChoice = await this.promptScopeChoice(
        'Save Event',
        'Do you want to save changes for this occurrence or the entire series?'
      );
      if (!scopeChoice) return; // Geannuleerd door gebruiker
      chosenScope = scopeChoice === 'single' ? 'THIS_INSTANCE' : 'ALL_EVENTS';
    }

    // 2. Bepaal de juiste start- en einddatum
    let finalStartDate: Date;
    let finalEndDate: Date;

    if (this.isEditMode && isExistingRecurring && chosenScope === 'ALL_EVENTS') {
      // HELE REEKS BEWERKEN:
      // Behoud het oorspronkelijke jaar/maand/dag van het master-event (bijv. 1 januari), 
      // maar koppel daar de nieuw gekozen tijd uit de dropdown aan vast.
      finalStartDate = this.combineDateAndTime(this.originalMasterStartDate, this.startTimeStr);
      finalEndDate = this.combineDateAndTime(this.originalMasterEndDate, this.endTimeStr);
    } else {
      // ENKELE INSTANTIE OF NIEUW EVENT:
      // Gebruik de geselecteerde datum uit de datepicker/modal (bijv. 23 oktober)
      finalStartDate = this.combineDateAndTime(this.event.startDate, this.startTimeStr);
      finalEndDate = this.combineDateAndTime(this.event.endDate, this.endTimeStr);
    }

    // 3. Bouw de schone DTO payload op voor de backend
    const isSingleInstance = chosenScope === 'THIS_INSTANCE';

    const payload = {
      ...this.event,
      startDate: finalStartDate.toISOString(),
      endDate: finalEndDate.toISOString(),
      isAllDay: this.isAllDay,
      attendees: this.attendees, // List<String> array

      // Bij een enkele instantie (uitzondering) vervalt de RRULE voor dit specifieke record
      rrule: isSingleInstance ? null : (this.event.rrule || this.generateRRule(finalStartDate)),
      isRecurring: isSingleInstance ? false : (this.recurrenceFrequency !== 'NONE' || !!this.event.rrule),

      // Stuur de scope en de datum van de specifieke herhaling mee
      editScope: chosenScope,
      instanceStartDate: isSingleInstance
        ? this.combineDateAndTime(this.event.startDate, this.startTimeStr).toISOString()
        : undefined
    };

    this.dialogRef.close({ action: 'save', event: payload });
  }

  async onDelete() {
    const isExistingRecurring = !!(this.event.isRecurring || this.event.rrule || this.event.recurringEventId);
    let chosenScope: 'THIS_INSTANCE' | 'ALL_EVENTS' = 'ALL_EVENTS';

    if (isExistingRecurring) {
      const scopeChoice = await this.promptScopeChoice(
        'Delete Event',
        'Do you want to delete only this occurrence or the entire series?'
      );
      if (!scopeChoice) return; // Geannuleerd
      chosenScope = scopeChoice === 'single' ? 'THIS_INSTANCE' : 'ALL_EVENTS';
    } else {
      const confirm = await firstValueFrom(
        this.matDialog.open(ConfirmDialogComponent, {
          width: '400px',
          data: {
            title: 'Delete Event',
            message: `Are you sure you want to delete "${this.event.title}"?`
          }
        }).afterClosed()
      );
      if (!confirm) return;
    }

    const instanceStartDate = this.combineDateAndTime(this.event.startDate, this.startTimeStr);

    // Stuur enkel de intentie en instantiedatum naar de backend.
    // Geen exdate-strings meer samenstellen in Angular!
    this.dialogRef.close({
      action: 'delete',
      payload: {
        id: this.event.id,
        eventId: this.event.eventId,
        calendarId: this.event.calendarId,
        scope: chosenScope,
        recurringEventId: this.event.recurringEventId || this.event.eventId,
        instanceStartDate: instanceStartDate.toISOString()
      }
    });
  }

  private applyNewTimeOnly(originalDateInput: Date | string, newTimeStr: string): Date {
    if (!originalDateInput) return new Date();

    // 1. Behoud het oorspronkelijke jaar, de maand en de dag van het master-event
    const masterDate = new Date(originalDateInput);
    const year = masterDate.getFullYear();
    const month = masterDate.getMonth();
    const day = masterDate.getDate();

    // 2. Ontleed de nieuwe geselecteerde tijd uit de dropdown (bijv. "14:30")
    let hours = 0;
    let minutes = 0;

    if (!this.isAllDay && newTimeStr) {
      const parts = newTimeStr.split(':').map(Number);
      hours = parts[0] || 0;
      minutes = parts[1] || 0;
    }

    // 3. Bouw een nieuw Date object met de originele datum + de nieuwe tijd
    return new Date(year, month, day, hours, minutes, 0, 0);
  }

  private promptScopeChoice(title: string, message: string): Promise<'single' | 'series' | null> {
    const dialogRef = this.matDialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: title,
        message: message,
        // Tips voor je ConfirmDialogComponent: geef opties mee zoals 'This Instance' / 'All Events'
        buttons: [
          { label: 'This occurrence', value: 'single' },
          { label: 'All events in series', value: 'series' }
        ]
      }
    });

    return firstValueFrom(dialogRef.afterClosed());
  }

  private combineDateAndTime(dateInput: Date | string, timeStr: string): Date {
    if (!dateInput) return new Date();

    const rawDate = new Date(dateInput);
    const year = rawDate.getFullYear();
    const month = rawDate.getMonth();
    const day = rawDate.getDate();

    let hours = 0;
    let minutes = 0;

    if (!this.isAllDay && timeStr) {
      const parts = timeStr.split(':').map(Number);
      hours = parts[0] || 0;
      minutes = parts[1] || 0;
    }

    return new Date(year, month, day, hours, minutes, 0, 0);
  }

  private padZero(num: number): string {
    return num < 10 ? `0${num}` : `${num}`;
  }

  onStartTimeChange(newStartTime: string) {
    if (!newStartTime) return;

    // Vervang eventuele punten door dubbele punten (bijv. 13.40 -> 13:40)
    const normalizedTime = newStartTime.replace('.', ':');
    const parts = normalizedTime.split(':');

    if (parts.length === 2) {
      const h = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);

      if (!isNaN(h) && !isNaN(m)) {
        const endMinutes = (h * 60 + m + 30) % (24 * 60);
        const endH = Math.floor(endMinutes / 60);
        const endM = endMinutes % 60;
        this.endTimeStr = `${this.padZero(endH)}:${this.padZero(endM)}`;
      }
    }
  }

  scrollToSelectedTime(selectedTime: string) {
    setTimeout(() => {
      // Zoek binnen de optionElements naar degene die daadwerkelijk zichtbaar/getekend is
      const selectedOption = this.optionElements.find(opt => {
        const hostEl = opt._getHostElement();
        return opt.value === selectedTime && hostEl.offsetParent !== null;
      });

      if (selectedOption) {
        selectedOption._getHostElement().scrollIntoView({ block: 'center' });
      }
    }, 0);
  }

  openRecurrenceDialog() {
    const dialogRef = this.matDialog.open(RecurrenceDialogComponent, {
      width: '360px',
      disableClose: false,
      autoFocus: false,
      data: {
        rruleString: this.event.rrule,
        startDate: this.event.startDate
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.event.rrule = result.rruleString;
        this.recurrenceSummaryText = result.displayText;
      }
    });
  }

  clearRecurrence(event: MouseEvent) {
    event.stopPropagation();
    this.event.rrule = '';
    this.recurrenceSummaryText = '';
  }
}